import {
  AutocompleteWithLabel,
  CountrySelectorForHeader,
  InputWithLabel,
  SelectWithLabel,
  TextAreaWithLabel
} from '@adotel/components'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { Grid, MenuItem, Switch, Typography } from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import MeetingRoomIcon from '@material-ui/icons/MeetingRoom'
import NoMeetingRoomIcon from '@material-ui/icons/NoMeetingRoom'
import { FormikErrors, FormikTouched } from 'formik'
import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { ConfirmActionModal } from '../../components/ConfirmActionModal'
import SectionTitle from '../../components/SectionTitle'
import { BOARD_TYPES_QUERY } from '../../graphql/boardTypes'
import { MODIFY_RATE_PLAN_ACTIVE_MUTATION } from '../../graphql/modifyRateplan'
import { PROVIDERS_QUERY } from '../../graphql/providerQueries'
import { RATEPLANS_QUERY } from '../../graphql/rateplan'
import { DELETE_RATEPLAN } from '../../graphql/rateplanDelete'
import { countries, CountryType } from '../../helpers/countriesList'
import {
  isFaAvailableInLanguages,
  getRoleFromStorage
} from '../../helpers/userData'
import { getTranslate, lang } from '../../localization'
import { ROLE } from '../../staticRoleManagement/types'
import { ProviderBrief } from '../../types/provider'
import {
  RatePlanBrief,
  RatePlanForGet
  // RatePlanForModify
} from '../../types/rateplan'
import { RoomTypeBrief } from '../../types/roomtype'

interface RateplanBasicDetailsProps {
  values: RatePlanForGet
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void
  errors: FormikErrors<RatePlanForGet>
  touched: FormikTouched<RatePlanForGet>
  handleBlur: any
  isSubmitting: any
  classes: any
  roomTypes: RoomTypeBrief[]
  loadingForRoomTypes: boolean
  isPhysical: boolean
  propertyCode: string
}

export const RateplanBasicDetailsForModify: React.FC<
  RateplanBasicDetailsProps
> = ({
  values,
  setFieldValue,
  errors,
  touched,
  handleBlur,
  isSubmitting,
  classes,
  roomTypes,
  loadingForRoomTypes,
  isPhysical,
  propertyCode
}) => {
  const history = useHistory()
  const params = useParams<{
    accommodationID: string
    propertyCode: string
    id: string
  }>()
  const [selectedCountryForName, setselectedCountryForName] =
    useState<CountryType>(countries[0])
  const [selectedCountryForPolicy, setselectedCountryForPolicy] =
    useState<CountryType>(countries[0])
  const { data, loading } = useQuery<{ boardTypes: string[] }>(
    BOARD_TYPES_QUERY
  )
  const { data: providerData, loading: loadingForProvider } = useQuery<{
    providers: ProviderBrief[]
  }>(PROVIDERS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only'
  })

  const foundProvider = providerData?.providers.find(
    (p) => p.displayProviderCode === values.basicDetails.displayProviderCode
  )

  const [autoCompleteForProvider, setAutoCompleteForProvider] = useState<any>({
    value: foundProvider?.providerCode,
    label: `${foundProvider?.name} - ${foundProvider?.displayProviderCode}`
  })

  useEffect(() => {
    if (loadingForProvider) {
      return
    }
    setAutoCompleteForProvider({
      value: foundProvider?.providerCode,
      label: `${foundProvider?.name} - ${foundProvider?.displayProviderCode}`
    })
  }, [loadingForProvider, values.basicDetails.providerCode])

  const foundRoomType = roomTypes.find(
    (rt) => rt.displayRoomTypeCode === values.basicDetails.displayRoomTypeCode
  )

  const [autoCompleteForRoomType, setAutoCompleteForRoomType] = useState<any>({
    value: foundRoomType?.id,
    label: foundRoomType?.name,
    code: values.basicDetails.code
  })

  const [autoCompleteForRatePlan, setAutoCompleteForRatePlan] =
    useState<any>(null)

  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false)
  const [activeModalIsOpen, setActiveModalIsOpen] = useState(false)

  const [getRatePlans, { data: ratePlanData, loading: loadingForRatePlan }] =
    useLazyQuery<{
      ratePlans: RatePlanBrief[]
    }>(RATEPLANS_QUERY, {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      onCompleted: (resp) => {
        const foundRatePlan = resp.ratePlans.find(
          (ele) =>
            ele.displayRatePlanCode === values.basicDetails.parentRatePlanCode
        )

        setAutoCompleteForRatePlan({
          value: foundRatePlan?.id,
          label: foundRatePlan?.name,
          code: values.basicDetails.code
        })
      }
    })

  const [deleteRateplan, { loading: loadingForDeleteRatePlan }] = useMutation(
    DELETE_RATEPLAN,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (resp) => {
        toast.success(resp.deleteRatePlan.message)
        history.push(
          `/${lang}/accommodations/${params.accommodationID}/${params.propertyCode}/rate-plans/list`
        )
      },
      onError: (err) => {
        toast.error(err.message)
      }
    }
  )

  const [modifyActiveRateplan, { loading: loadingForActiveRateplan }] =
    useMutation(MODIFY_RATE_PLAN_ACTIVE_MUTATION, {
      notifyOnNetworkStatusChange: true,
      onCompleted: (resp) => {
        toast.success(resp.modifyActiveRatePlan.message)
        setActiveModalIsOpen(false)
        history.push(
          `/${lang}/accommodations/${params.accommodationID}/${params.propertyCode}/rate-plans/list`
        )
      },
      onError: (err) => {
        toast.error(err.message)
      }
    })

  useEffect(() => {
    if (propertyCode && foundRoomType) {
      getRatePlans({
        variables: {
          propertyCode: propertyCode,
          roomTypeCode: foundRoomType?.parentRoomTypeCode,
          showDeletedRatePlans: false,
          showDeactivedRatePlans: true
        }
      })
    }
  }, [propertyCode, foundRoomType])

  useEffect(() => {
    if (loadingForRoomTypes) {
      return
    }

    setAutoCompleteForRoomType({
      value: foundRoomType?.id,
      label: foundRoomType?.name,
      code: values.basicDetails.code
    })
  }, [values.basicDetails.roomTypeCode, loadingForRoomTypes, roomTypes])

  // useEffect(() => {
  //   if (values.basicDetails.type === 'Virtual') {
  //     setFieldValue('basicDetails.boardType', '')
  //   }
  // }, [values.basicDetails.type])

  const isRoleAdmin = getRoleFromStorage() === ROLE.ADMIN

  return (
    <>
      <div className={classes.titleContainer}>
        <SectionTitle
          title="Basic Details"
          marginBottom
          deleteIcon
          onClickOnDeleteIcon={() => {
            setDeleteModalIsOpen(true)
          }}
          disabled={!!params.id ? values.basicDetails.deleted : true}
        />
      </div>
      <div className={classes.fieldsContainer}>
        <SelectWithLabel
          name="basicDetails.type"
          label={getTranslate('Type')}
          fullWidth
          value={values.basicDetails.type}
          onChange={(event) =>
            setFieldValue('basicDetails.type', event.target.value)
          }
          error={
            (touched.basicDetails?.type && !!errors.basicDetails?.type) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.type
              ? getTranslate(errors.basicDetails?.type || '')
              : undefined
          }
          disabled={true}
          size="small">
          <MenuItem key="Physical" value="Physical">
            {getTranslate('Physical')}
          </MenuItem>
          <MenuItem key="Virtual" value="Virtual">
            {getTranslate('Virtual')}
          </MenuItem>
        </SelectWithLabel>
        <AutocompleteWithLabel
          label={getTranslate(isPhysical ? 'Room Type' : 'Parent Room Type')}
          placeholder={getTranslate(
            isPhysical ? 'Select Room type' : 'Select Parent Room type'
          )}
          name="basicDetails.roomTypeCode"
          id="basicDetails.roomTypeCode"
          onChange={(_, v: any) => {
            setAutoCompleteForRoomType({
              value: v.value,
              label: v.label,
              code: v.code
            })
            setFieldValue('basicDetails.roomTypeCode', v.code)
          }}
          getOptionLabel={(option) => option.label || ''}
          getOptionSelected={(option, value) => option.value === value.value}
          options={
            roomTypes
              .filter(
                (ele) => ele.type === (isPhysical ? 'Physical' : 'Virtual')
              )
              .map((item) => ({
                label: `${item.name} (${item.displayRoomTypeCode})`,
                value: item.id || '',
                code: item.displayRoomTypeCode
              })) || []
          }
          size="small"
          error={
            (touched.basicDetails?.roomTypeCode &&
              !!errors.basicDetails?.roomTypeCode) ||
            undefined
          }
          value={autoCompleteForRoomType}
          includeInputInList
          helpertext={
            touched.basicDetails?.roomTypeCode
              ? getTranslate(errors.basicDetails?.roomTypeCode || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={true}
          inputSize="small"
          loading={loadingForRoomTypes}
          loadingText={getTranslate('Loading') + '...'}
        />
        <AutocompleteWithLabel
          label={getTranslate('Provider')}
          placeholder={getTranslate('Select Provider')}
          name="basicDetails.providerCode"
          id="basicDetails.providerCode"
          inputSize="small"
          value={autoCompleteForProvider}
          getOptionLabel={(option) => option.label || ''}
          getOptionSelected={(option, value) => option.value === value.value}
          onChange={(_, v: any) => {
            setAutoCompleteForProvider({
              value: v.value,
              label: v.label
            })
          }}
          options={
            providerData?.providers
              ? providerData?.providers.map((p) => ({
                  label: `${p.name} - (${p.displayProviderCode})`,
                  value: p.id
                }))
              : []
          }
          includeInputInList
          onBlur={handleBlur}
          loading={loadingForProvider}
          loadingText={getTranslate('Loading') + '...'}
          disabled={true}
        />
      </div>
      <div className={classes.fieldsContainer}>
        {!isPhysical && (
          <AutocompleteWithLabel
            label={getTranslate('Parent Rate Plan')}
            placeholder={getTranslate('Select Parent Rate Plan')}
            name="basicDetails.parentRatePlanCode"
            id="basicDetails.parentRatePlanCode"
            onChange={(_, v: any) => {
              if (v) {
                setAutoCompleteForRatePlan({
                  value: v.value,
                  label: v.label,
                  code: v.code
                })
                setFieldValue('basicDetails.parentRatePlanCode', v.code)
              } else {
                setAutoCompleteForRatePlan(null)
                setFieldValue('basicDetails.parentRatePlanCode', '')
              }
            }}
            getOptionLabel={(option) => option.label || ''}
            getOptionSelected={(option, value) => option.value === value.value}
            options={
              ratePlanData?.ratePlans.map((item) => ({
                label: `${item.name} (${item.displayRatePlanCode})`,
                value: item.id || '',
                code: item.displayRatePlanCode
              })) || []
            }
            size="small"
            error={
              (touched.basicDetails?.parentRatePlanCode &&
                !!errors.basicDetails?.parentRatePlanCode) ||
              undefined
            }
            value={autoCompleteForRatePlan}
            includeInputInList
            helpertext={
              touched.basicDetails?.parentRatePlanCode
                ? getTranslate(errors.basicDetails?.parentRatePlanCode || '')
                : undefined
            }
            onBlur={handleBlur}
            disabled={true}
            inputSize="small"
            loading={loadingForRatePlan}
            loadingText={getTranslate('Loading') + '...'}
          />
        )}
        <InputWithLabel
          label={getTranslate('Code')}
          placeholder={getTranslate('Choose a Code for rate plan')}
          id="basicDetails.code"
          name="basicDetails.code"
          value={values.basicDetails.displayRatePlanCode}
          onChange={(event) => {
            setFieldValue('basicDetails.code', event.currentTarget.value)
            setFieldValue(
              'basicDetails.displayRatePlanCode',
              event.currentTarget.value
            )
          }}
          error={
            (touched.basicDetails?.code && !!errors.basicDetails?.code) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.code
              ? getTranslate(errors.basicDetails?.code || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting || !!params.id}
          inputSize="small"
        />
        <InputWithLabel
          label={getTranslate('Name')}
          placeholder={getTranslate('Enter the rate plan name')}
          name="basicDetails.name"
          id="basicDetails.name"
          value={values.basicDetails.name}
          onChange={(event) =>
            setFieldValue('basicDetails.name', event.currentTarget.value)
          }
          error={
            (touched.basicDetails?.name && !!errors.basicDetails?.name) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.name
              ? getTranslate(errors.basicDetails?.name || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        {isFaAvailableInLanguages() && (
          <InputWithLabel
            placeholder={
              lang === 'fa' && selectedCountryForName.code === 'IR'
                ? `${getTranslate('Enter the rate plan name in')} ${
                    selectedCountryForName.nativeLanguage
                  }`
                : `${getTranslate('Enter the rate plan name in')} ${
                    selectedCountryForName.language
                  }`
            }
            label={`${
              lang === 'en' ? selectedCountryForName.language : ''
            } ${getTranslate('Name')} ${
              lang === 'fa' && selectedCountryForName.code === 'IR'
                ? selectedCountryForName.nativeLanguage
                : lang === 'fa'
                ? selectedCountryForName.language
                : ''
            }`}
            id={`basicDetails.nameTranslatedList[${values.basicDetails.nameTranslatedList.findIndex(
              (item) => item.label === selectedCountryForName.label
            )}].value`}
            name={`basicDetails.nameTranslatedList[${values.basicDetails.nameTranslatedList.findIndex(
              (item) => item.label === selectedCountryForName.label
            )}].value`}
            value={
              values.basicDetails.nameTranslatedList.find(
                (item) => item.label === selectedCountryForName.label
              )?.value
            }
            onChange={(event: {
              currentTarget: { value: React.SetStateAction<string> }
            }) => {
              setFieldValue(
                `basicDetails.nameTranslatedList[${values.basicDetails.nameTranslatedList.findIndex(
                  (item) => item.label === selectedCountryForName.label
                )}].value`,
                event.currentTarget.value
              )
            }}
            error={
              (touched.basicDetails?.nameTranslatedList &&
                !!errors.basicDetails?.nameTranslatedList) ||
              undefined
            }
            helpertext={
              touched.basicDetails?.nameTranslatedList
                ? (
                    errors.basicDetails?.nameTranslatedList as FormikErrors<{
                      value: string
                    }>[]
                  )
                    ?.filter((item) => item)
                    .map((item) => getTranslate(item.value || ''))
                    .join('')
                : undefined
            }
            onBlur={handleBlur}
            disabled={isSubmitting}
            inputSize="small"
            rightHeader={
              <CountrySelectorForHeader
                selectedCountry={selectedCountryForName}
                onChange={(item: CountryType) => {
                  const translatedName = {
                    label: item.label,
                    nativeLabel: item.nativeLanguage,
                    value: ''
                  }
                  const tempTranslatedArr = [
                    ...values.basicDetails.nameTranslatedList,
                    translatedName
                  ]
                  setFieldValue(
                    'basicDetails.nameTranslatedList',
                    tempTranslatedArr
                  )
                  setselectedCountryForName(item)
                }}
              />
            }
          />
        )}
      </div>
      <div className={classes.fieldsContainer}>
        {(!!params.id || isPhysical) && (
          <>
            <SelectWithLabel
              label={getTranslate('Board Type')}
              placeholder={getTranslate('Select a Board Type')}
              name="basicDetails.boardType"
              id="basicDetails.boardType"
              value={values.basicDetails.boardType}
              onChange={(event) =>
                setFieldValue('basicDetails.boardType', event.target.value)
              }
              error={
                (touched.basicDetails?.boardType &&
                  !!errors.basicDetails?.boardType) ||
                undefined
              }
              helpertext={
                touched.basicDetails?.boardType
                  ? getTranslate(errors.basicDetails?.boardType || '')
                  : undefined
              }
              disabled={!!params.id}
              size="small">
              {(loading ? [] : data?.boardTypes)?.map((singleBoardType) => (
                <MenuItem key={singleBoardType} value={singleBoardType}>
                  {singleBoardType}
                </MenuItem>
              ))}
            </SelectWithLabel>
            <div />
            <div />
          </>
        )}
      </div>
      <div className={classes.fieldsContainer}>
        <TextAreaWithLabel
          label={getTranslate('Rate Policy')}
          placeholder={getTranslate('Enter the rate plan Rate Policy')}
          value={values.basicDetails.ratePolicy}
          onChange={(event) =>
            setFieldValue('basicDetails.ratePolicy', event.currentTarget.value)
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
          rows={7}
          error={
            (touched.basicDetails?.ratePolicy &&
              !!errors.basicDetails?.ratePolicy) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.ratePolicy
              ? getTranslate(errors.basicDetails?.ratePolicy || '')
              : undefined
          }
        />
        {isFaAvailableInLanguages() && (
          <TextAreaWithLabel
            placeholder={
              lang === 'fa' && selectedCountryForPolicy.code === 'IR'
                ? `${getTranslate('Enter the rate plan Rate Policy in')} ${
                    selectedCountryForPolicy.nativeLanguage
                  }`
                : `${getTranslate('Enter the rate plan Rate Policy in')} ${
                    selectedCountryForPolicy.language
                  }`
            }
            label={`${
              lang === 'en' ? selectedCountryForPolicy.language : ''
            } ${getTranslate('Rate Policy')} ${
              lang === 'fa' && selectedCountryForPolicy.code === 'IR'
                ? selectedCountryForPolicy.nativeLanguage
                : lang === 'fa'
                ? selectedCountryForPolicy.language
                : ''
            }`}
            id={`basicDetails.ratePolicyTranslatedList[${values.basicDetails.ratePolicyTranslatedList.findIndex(
              (item) => item.label === selectedCountryForPolicy.label
            )}].value`}
            name={`basicDetails.ratePolicyTranslatedList[${values.basicDetails.ratePolicyTranslatedList.findIndex(
              (item) => item.label === selectedCountryForPolicy.label
            )}].value`}
            value={
              values.basicDetails.ratePolicyTranslatedList?.find(
                (item) => item.label === selectedCountryForPolicy.label
              )?.value
            }
            onChange={(event: {
              currentTarget: { value: React.SetStateAction<string> }
            }) => {
              setFieldValue(
                `basicDetails.ratePolicyTranslatedList[${values.basicDetails.nameTranslatedList.findIndex(
                  (item) => item.label === selectedCountryForPolicy.label
                )}].value`,
                event.currentTarget.value
              )
            }}
            onBlur={handleBlur}
            disabled={isSubmitting}
            inputSize="small"
            rows={7}
            error={
              (touched.basicDetails?.ratePolicyTranslatedList &&
                !!errors.basicDetails?.ratePolicyTranslatedList) ||
              undefined
            }
            helpertext={
              touched.basicDetails?.ratePolicyTranslatedList
                ? (
                    errors.basicDetails
                      ?.ratePolicyTranslatedList as FormikErrors<{
                      value: string
                    }>[]
                  )
                    ?.filter((item) => item)
                    .map((item) => getTranslate(item.value || ''))
                    .join('')
                : undefined
            }
            rightHeader={
              <CountrySelectorForHeader
                selectedCountry={selectedCountryForPolicy}
                onChange={(item: CountryType) => {
                  const translatedDescription = {
                    label: item.label,
                    nativeLabel: item.nativeLanguage,
                    value: ''
                  }
                  const tempTranslatedArr = [
                    ...values.basicDetails.ratePolicyTranslatedList,
                    values.basicDetails.ratePolicyTranslatedList.find(
                      (singleDescription) =>
                        singleDescription.label === item.label
                    )
                      ? undefined
                      : translatedDescription
                  ]
                  setFieldValue(
                    'basicDetails.ratePolicyTranslatedList',
                    tempTranslatedArr
                  )
                  setselectedCountryForPolicy(item)
                }}
              />
            }
          />
        )}
        {isRoleAdmin && (
          <div className={classes.switchContainer}>
            <Typography variant="h4">
              {getTranslate('Is this ratePlan active?')}
            </Typography>
            <Typography component="div" className={classes.switch}>
              <Grid
                component="label"
                container
                alignItems="center"
                justify="flex-start">
                <Grid item>{getTranslate('No')}</Grid>
                <Grid item>
                  <Switch
                    name="active"
                    color="primary"
                    onChange={() => {
                      setActiveModalIsOpen(true)
                    }}
                    checked={values.basicDetails.active}
                    disabled={!params.id || values.basicDetails.deleted}
                  />
                </Grid>
                <Grid item>{getTranslate('Yes')}</Grid>
              </Grid>
            </Typography>
          </div>
        )}
      </div>
      {deleteModalIsOpen && (
        <ConfirmActionModal
          icon={<DeleteIcon className={classes.icon} />}
          title={'RatePlan is about to be deleted'}
          subtitle={'Are you sure you want to delete it permanently?'}
          signature={'Delete Me'}
          onClose={() => setDeleteModalIsOpen(false)}
          isOpen={deleteModalIsOpen}
          onClick={() => {
            deleteRateplan({
              variables: {
                input: {
                  propertyCode: propertyCode,
                  roomTypeCode: values.basicDetails.roomTypeCode,
                  roomTypeNames: {
                    EN: values.basicDetails.roomTypeName,
                    FA: values.basicDetails.roomTypeNameTranslatedList
                      ? values.basicDetails.roomTypeNameTranslatedList[0].value
                      : ''
                  },
                  ratePlanCode: values.basicDetails.code,
                  ratePlanNames: {
                    EN: values.basicDetails.name,
                    FA: values.basicDetails.nameTranslatedList[0].value
                  }
                }
              }
            })
          }}
          loading={loadingForDeleteRatePlan}
        />
      )}
      {activeModalIsOpen && (
        <ConfirmActionModal
          icon={
            values.basicDetails.active ? (
              <NoMeetingRoomIcon className={classes.icon} />
            ) : (
              <MeetingRoomIcon className={classes.icon} />
            )
          }
          title={
            values.basicDetails.active
              ? getTranslate('The ratePlan is about to be deactivated')
              : getTranslate('The ratePlan is about to be activated')
          }
          subtitle={
            values.basicDetails.active
              ? getTranslate('Are you sure you want to deactive it?')
              : getTranslate('Are you sure you want to active it?')
          }
          signature={values.basicDetails.active ? 'Deactive Me' : 'Active Me'}
          onClose={() => setActiveModalIsOpen(false)}
          isOpen={activeModalIsOpen}
          onClick={() => {
            modifyActiveRateplan({
              variables: {
                propertyCode: propertyCode,
                roomTypeCode: values.basicDetails.roomTypeCode,
                ratePlanCode: values.basicDetails.code,
                active: !values.basicDetails.active
              }
            })
          }}
          loading={loadingForActiveRateplan}
        />
      )}
    </>
  )
}
